import {
  boolean,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Course Statuses
// ---------------------------------------------------------------------------

export const COURSE_STATUSES = ["draft", "published", "archived"] as const;
export type CourseStatus = (typeof COURSE_STATUSES)[number];

export const LESSON_TYPES = ["video", "text", "pdf", "quiz", "exercise"] as const;
export type LessonType = (typeof LESSON_TYPES)[number];

export const ENROLLMENT_STATUSES = ["active", "completed", "dropped"] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Quiz Types
// ---------------------------------------------------------------------------

export interface QuizOption {
  id: string;
  text: string;
}

export interface QuizQuestion {
  id: string;
  type: "multiple_choice" | "true_false";
  text: string;
  options?: QuizOption[];
  correctAnswerId: string;
  points: number;
  explanation?: string;
}

export interface QuizConfig {
  questions: QuizQuestion[];
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showExplanations: boolean;
  passingScore: number;
  maxAttempts: number;
  timeLimitMinutes?: number;
}

// ---------------------------------------------------------------------------
// Courses
// ---------------------------------------------------------------------------

export const courses = pgTable("courses", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  title: text("title").notNull(),
  slug: text("slug").notNull(),
  description: text("description"),
  thumbnailR2Key: text("thumbnail_r2_key"),
  status: text("status").notNull().default("draft"),
  accessTierIds: jsonb("access_tier_ids").$type<string[]>().default([]).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_courses_slug").on(table.tenantId, table.slug),
  index("idx_courses_tenant").on(table.tenantId, table.status),
  index("idx_courses_sort").on(table.tenantId, table.sortOrder),
]);

// ---------------------------------------------------------------------------
// Course Modules
// ---------------------------------------------------------------------------

export const courseModules = pgTable("course_modules", {
  id: uuid("id").primaryKey().defaultRandom(),
  courseId: uuid("course_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  title: text("title").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_course_modules_course").on(table.courseId, table.sortOrder),
  index("idx_course_modules_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// Course Lessons
// ---------------------------------------------------------------------------

export const courseLessons = pgTable("course_lessons", {
  id: uuid("id").primaryKey().defaultRandom(),
  moduleId: uuid("module_id").notNull(),
  courseId: uuid("course_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  title: text("title").notNull(),
  lessonType: text("lesson_type").notNull(), // video, text, pdf, quiz, exercise
  contentPageId: uuid("content_page_id"), // FK to page builder pages (for text/exercise types)
  externalVideoUrl: text("external_video_url"), // YouTube/Vimeo/Wistia/Bunny/Loom URL
  pdfR2Key: text("pdf_r2_key"), // R2 key for PDF content
  quizConfig: jsonb("quiz_config").$type<QuizConfig>(),
  sortOrder: integer("sort_order").notNull().default(0),
  estimatedMinutes: integer("estimated_minutes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_course_lessons_module").on(table.moduleId, table.sortOrder),
  index("idx_course_lessons_course").on(table.courseId),
  index("idx_course_lessons_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// Course Enrollments
// ---------------------------------------------------------------------------

export const courseEnrollments = pgTable("course_enrollments", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  courseId: uuid("course_id").notNull(),
  contactId: uuid("contact_id").notNull(),
  progressPercent: integer("progress_percent").notNull().default(0),
  status: text("status").notNull().default("active"),
  certificateToken: text("certificate_token"), // nanoid 21, generated at completion
  enrolledAt: timestamp("enrolled_at", { withTimezone: true }).defaultNow().notNull(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_enrollments_course_contact").on(table.courseId, table.contactId),
  index("idx_enrollments_tenant").on(table.tenantId),
  index("idx_enrollments_contact").on(table.tenantId, table.contactId),
  index("idx_enrollments_course").on(table.tenantId, table.courseId, table.status),
]);

// ---------------------------------------------------------------------------
// Course Lesson Completions (junction table — replaces JSONB array)
// ---------------------------------------------------------------------------

export const courseLessonCompletions = pgTable("course_lesson_completions", {
  id: uuid("id").primaryKey().defaultRandom(),
  enrollmentId: uuid("enrollment_id").notNull(),
  lessonId: uuid("lesson_id").notNull(),
  completedAt: timestamp("completed_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_lesson_completions").on(table.enrollmentId, table.lessonId),
  index("idx_lesson_completions_enrollment").on(table.enrollmentId),
]);

// ---------------------------------------------------------------------------
// Course Quiz Attempts
// ---------------------------------------------------------------------------

export const courseQuizAttempts = pgTable("course_quiz_attempts", {
  id: uuid("id").primaryKey().defaultRandom(),
  enrollmentId: uuid("enrollment_id").notNull(),
  lessonId: uuid("lesson_id").notNull(),
  attemptNumber: integer("attempt_number").notNull().default(1),
  answers: jsonb("answers").$type<Record<string, string>>().notNull(), // questionId → answerId
  score: integer("score").notNull(),
  totalPoints: integer("total_points").notNull(),
  passed: boolean("passed").notNull(),
  submittedAt: timestamp("submitted_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_quiz_attempts").on(table.enrollmentId, table.lessonId, table.attemptNumber),
  index("idx_quiz_attempts_enrollment").on(table.enrollmentId, table.lessonId),
]);
